import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Theme {
  mode: 'light' | 'dark';
  primary: string;
  secondary: string;
  background: string;
  surface: string;
  surfaceAlt: string;
  text: string;
  textSecondary: string;
  border: string;
  borderLight: string;
  error: string;
  success: string;
  warning: string;
}

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const lightTheme: Theme = {
  mode: 'light',
  primary: '#00BBA2',  // Pierag teal
  secondary: '#0d0e1a',
  background: '#ffffff',
  surface: '#f8fafc',
  surfaceAlt: '#f1f5f9',
  text: '#111827',
  textSecondary: '#6b7280',
  border: '#e2e8f0',
  borderLight: '#f1f5f9',
  error: '#ef4444',
  success: '#10b981',
  warning: '#f59e0b'
};

const darkTheme: Theme = {
  mode: 'dark',
  primary: '#00BBA2',  // Pierag teal
  secondary: '#00FFCD',
  background: '#0d0e1a',
  surface: '#1a1f2e',
  surfaceAlt: '#242938',
  text: '#f1f5f9',
  textSecondary: '#94a3b8',
  border: '#2d3448',
  borderLight: '#374151',
  error: '#ef4444',
  success: '#10b981',
  warning: '#f59e0b'
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<'light' | 'dark'>('light');

  const theme = mode === 'light' ? lightTheme : darkTheme;

  const toggleTheme = () => {
    setMode(prev => prev === 'light' ? 'dark' : 'light');
  };

  useEffect(() => {
    // Load theme preference from localStorage
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark' || savedTheme === 'light') {
      setMode(savedTheme);
    }
  }, []);

  useEffect(() => {
    // Save theme preference to localStorage
    localStorage.setItem('theme', mode);
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
